import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily, atomWithRefresh } from "jotai/utils";

import { Logger } from "@moment/logging";

import { getCache } from "../_interop/cache";
import { $accessTokenFunc, $isAuthenticated } from "../authenication";
import { $document, docsListItemRemoteEntry } from "../document-list";
import { env } from "../env";
// Import via the environments barrel (not the atom file directly) so test
// files that `mock.module("../environments", …)` can shadow this too.
import { $selectedEnvironmentName } from "../environments";
import { $internet } from "../utils";
import {
	type CreateSecretInput,
	type DeleteSecretInput,
	type ListSecretsResponse,
	ListSecretsResponseSchema,
	SecretsProxyError,
	type SecretsproxyContext,
} from "./secretsproxy.types";

const logger = new Logger("secretsproxy");

export type SecretsProxyAPI = {
	secrets: {
		list: (token: string) => Promise<ListSecretsResponse>;
		create: (input: CreateSecretInput, token: string) => Promise<void>;
		delete: (input: DeleteSecretInput, token: string) => Promise<void>;
	};
};

const buildHeaders = (token: string): Headers => {
	const headers = new Headers();
	headers.set("Accept", "application/json");
	headers.set("Content-Type", "application/json");
	headers.set("Authorization", `Bearer ${token}`);
	return headers;
};

const createSecretsProxyAPI = (baseUrl: string): SecretsProxyAPI => ({
	secrets: {
		list: async (token) => {
			const resp = await fetch(`${baseUrl}/internal/v1/secrets`, {
				method: "GET",
				headers: buildHeaders(token),
			});
			if (!resp.ok) {
				throw new SecretsProxyError(
					`Failed to list secrets: ${resp.statusText}`,
					resp.status,
					resp
				);
			}
			return ListSecretsResponseSchema.parse(await resp.json());
		},
		create: async ({ workspaceSlug, profile, tokenName, secret }, token) => {
			const resp = await fetch(`${baseUrl}/internal/v1/secrets`, {
				method: "POST",
				headers: buildHeaders(token),
				body: JSON.stringify({
					workspace_slug: workspaceSlug,
					profile,
					token_name: tokenName,
					secret,
				}),
			});
			if (!resp.ok) {
				throw new SecretsProxyError(
					`Failed to create secret: ${resp.statusText}`,
					resp.status,
					resp
				);
			}
		},
		delete: async ({ workspaceSlug, profile, tokenName }, token) => {
			const path = `/internal/v1/secrets/${encodeURIComponent(workspaceSlug)}/${encodeURIComponent(profile)}/${encodeURIComponent(tokenName)}`;
			const resp = await fetch(`${baseUrl}${path}`, {
				method: "DELETE",
				headers: buildHeaders(token),
			});
			if (!resp.ok && resp.status !== 404) {
				throw new SecretsProxyError(
					`Failed to delete secret: ${resp.statusText}`,
					resp.status,
					resp
				);
			}
		},
	},
});

const noOpSecretsProxyAPI: SecretsProxyAPI = {
	secrets: {
		list: async () => ({ workspaces: {} }),
		create: async () => {
			throw new SecretsProxyError("SecretsProxy not available");
		},
		delete: async () => {
			throw new SecretsProxyError("SecretsProxy not available");
		},
	},
};

export const $secretsProxy = atom((get) => {
	const isConnected = get($internet);
	if (env.secretsProxyUrl && isConnected) {
		return createSecretsProxyAPI(env.secretsProxyUrl);
	}
	return noOpSecretsProxyAPI;
});
$secretsProxy.debugLabel = "secretsProxy";

// In-memory draft for the secrets profile override. Mirrors the env atom
// pattern — a draft wins over the cached value until persistence completes.
const $draftSecretsProfileOverride = atom(undefined as string | null | undefined);
$draftSecretsProfileOverride.debugPrivate = true;

const $cachedSecretsProfileOverride = atom(async (): Promise<string | null> => {
	const cache = getCache();
	try {
		const settings = await cache.settings.get();
		return settings.selectedSecretsProfileOverride ?? null;
	} catch (error) {
		logger.error("Failed to read selectedSecretsProfileOverride from cache", { cause: error });
		return null;
	}
});
$cachedSecretsProfileOverride.debugPrivate = true;

/**
 * The secrets-profile override, or null if the user has not split env +
 * secrets profiles. UI shows a "Use a different secrets profile →" link that
 * writes through `$setSecretsProfileOverride`.
 */
export const $secretsProfileOverride = atom(async (get): Promise<string | null> => {
	const draft = get($draftSecretsProfileOverride);
	if (draft !== undefined) {
		return draft;
	}
	return await get($cachedSecretsProfileOverride);
});
$secretsProfileOverride.debugLabel = "secretsProfileOverride";

/**
 * Write atom: set or clear the secrets profile override. Pass null to re-link
 * secrets to the env profile. Pass a string to diverge.
 */
export const $setSecretsProfileOverride = atom(null, async (_get, set, next: string | null) => {
	set($draftSecretsProfileOverride, next);
	try {
		await getCache().settings.update({ selectedSecretsProfileOverride: next });
	} catch (error) {
		logger.error("Failed to persist selectedSecretsProfileOverride", { cause: error });
	}
});
$setSecretsProfileOverride.debugLabel = "setSecretsProfileOverride";

// Workspace slug the user picked for testing secrets against while working on
// a local document. Remote documents always win — their workspace is fixed by
// the `remote` entry. This atom is only consulted for local-only docs.
const $draftSecretsWorkspace = atom(undefined as string | null | undefined);
$draftSecretsWorkspace.debugPrivate = true;

const $cachedSecretsWorkspace = atom(async (): Promise<string | null> => {
	const cache = getCache();
	try {
		const settings = await cache.settings.get();
		return settings.selectedSecretsWorkspace ?? null;
	} catch (error) {
		logger.error("Failed to read selectedSecretsWorkspace from cache", { cause: error });
		return null;
	}
});
$cachedSecretsWorkspace.debugPrivate = true;

export const $selectedSecretsWorkspace = atom(async (get): Promise<string | null> => {
	const draft = get($draftSecretsWorkspace);
	if (draft !== undefined) {
		return draft;
	}
	return await get($cachedSecretsWorkspace);
});
$selectedSecretsWorkspace.debugLabel = "selectedSecretsWorkspace";

export const $setSelectedSecretsWorkspace = atom(null, async (_get, set, next: string | null) => {
	set($draftSecretsWorkspace, next);
	try {
		await getCache().settings.update({ selectedSecretsWorkspace: next });
	} catch (error) {
		logger.error("Failed to persist selectedSecretsWorkspace", { cause: error });
	}
});
$setSelectedSecretsWorkspace.debugLabel = "setSelectedSecretsWorkspace";

/**
 * Resolves the profile name that should be sent to secretsproxy. Falls back
 * to `$selectedEnvironmentName` when the user has not overridden — so by
 * default env + secrets profiles move together, but the user can split them
 * via `$setSecretsProfileOverride`.
 */
export const $selectedProfile = atom(async (get): Promise<string> => {
	try {
		const override = await get($secretsProfileOverride);
		if (override) {
			return override;
		}
		return (await get($selectedEnvironmentName)) || "default";
	} catch (error) {
		logger.error("Failed to read selected profile", { cause: error });
		return "default";
	}
});
$selectedProfile.debugLabel = "selectedProfile";

/**
 * Represents the outcome of a ListSecrets call:
 *  - `ok`        — server returned successfully. `data` is authoritative.
 *  - `unauthed`  — no auth token yet; treat as "unknown, don't decide."
 *  - `error`     — server failed (network, 5xx, etc). Callers should NOT
 *                  render secrets as "missing" because we genuinely don't
 *                  know the list; distinguish from an empty list.
 *
 * `data` is always present (empty on error/unauthed) so downstream merging
 * with the optimistic delta still produces a stable shape.
 */
export type WorkspaceSecretsLoadStatus = "ok" | "unauthed" | "error";

export type WorkspaceSecretsState = {
	status: WorkspaceSecretsLoadStatus;
	data: ListSecretsResponse["workspaces"];
};

export type WorkspaceSecretNamesState = {
	status: WorkspaceSecretsLoadStatus;
	names: string[];
};

export type WorkspaceSecretsByProfileState = {
	status: WorkspaceSecretsLoadStatus;
	data: Record<string, string[]>;
};

// Raw list of all secrets the logged-in user can see, keyed by workspace slug
// then by profile. Refreshable after mutations. AWS Secrets Manager is
// eventually consistent — a ListSecrets call right after a create/delete can
// return stale results, so the UI merges in `$optimisticSecretDelta` below.
export const $allWorkspaceSecrets = atomWithRefresh(async (get): Promise<WorkspaceSecretsState> => {
	if (!get($isAuthenticated)) {
		return { status: "unauthed", data: {} };
	}

	const getAccessTokenSilently = get($accessTokenFunc).getAccessTokenSilently;
	const token = getAccessTokenSilently ? await getAccessTokenSilently() : undefined;
	if (!token) {
		return { status: "unauthed", data: {} };
	}
	try {
		const api = get($secretsProxy);
		const response = await api.secrets.list(token);
		return { status: "ok", data: response.workspaces };
	} catch (error) {
		logger.error("Failed to fetch secrets", { cause: error });
		return { status: "error", data: {} };
	}
});
$allWorkspaceSecrets.debugLabel = "allWorkspaceSecrets";

// Status-only view for UI that needs to distinguish "server said no" from
// "we don't know yet." Avoids forcing every consumer to destructure the
// full state object.
export const $workspaceSecretsLoadStatus = atom(
	async (get): Promise<WorkspaceSecretsLoadStatus> => {
		const state = await get($allWorkspaceSecrets);
		return state.status;
	}
);
$workspaceSecretsLoadStatus.debugLabel = "workspaceSecretsLoadStatus";

// Optimistic delta applied on top of `$allWorkspaceSecrets`. `added` entries
// show up immediately after a successful create; `removed` entries disappear
// immediately after a successful delete. Both are safe to retain across
// refreshes — once the server list catches up, the union/filter reduces to
// a no-op.
type SecretDelta = {
	added: Record<string, Record<string, Set<string>>>;
	removed: Record<string, Record<string, Set<string>>>;
};

const $optimisticSecretDelta = atom({
	added: {},
	removed: {},
} as SecretDelta);
$optimisticSecretDelta.debugPrivate = true;

const removeFromBucket = (
	bucket: Record<string, Record<string, Set<string>>>,
	workspaceSlug: string,
	profile: string,
	name: string
): Record<string, Record<string, Set<string>>> => {
	const existing = bucket[workspaceSlug]?.[profile];
	if (!existing?.has(name)) {
		return bucket;
	}
	const nextBucket = { ...bucket };
	const nextWorkspace = { ...(nextBucket[workspaceSlug] ?? {}) };
	const nextSet = new Set(existing);
	nextSet.delete(name);
	nextWorkspace[profile] = nextSet;
	nextBucket[workspaceSlug] = nextWorkspace;
	return nextBucket;
};

const addOptimistic = (
	delta: SecretDelta,
	bucket: "added" | "removed",
	workspaceSlug: string,
	profile: string,
	name: string
): SecretDelta => {
	// The two buckets must be mutually exclusive per (slug, profile, name).
	// Otherwise a sequence like create → delete → create ends up with the
	// name in both buckets, and the merge (server ∪ added \ removed)
	// incorrectly hides it. Clear the opposite bucket before writing.
	const opposite: "added" | "removed" = bucket === "added" ? "removed" : "added";
	const clearedOpposite = removeFromBucket(delta[opposite], workspaceSlug, profile, name);

	const nextBucket = { ...delta[bucket] };
	const nextWorkspace = { ...(nextBucket[workspaceSlug] ?? {}) };
	const nextSet = new Set(nextWorkspace[profile] ?? []);
	nextSet.add(name);
	nextWorkspace[profile] = nextSet;
	nextBucket[workspaceSlug] = nextWorkspace;

	return {
		...delta,
		[bucket]: nextBucket,
		[opposite]: clearedOpposite,
	};
};

const mergeSecretNames = (
	state: WorkspaceSecretsState,
	delta: SecretDelta,
	workspaceSlug: string,
	profile: string
): string[] => {
	const fromServer = state.data[workspaceSlug]?.[profile] ?? [];
	const pendingAdd = delta.added[workspaceSlug]?.[profile];
	const pendingRemove = delta.removed[workspaceSlug]?.[profile];
	const merged = new Set(fromServer);
	pendingAdd?.forEach((name) => merged.add(name));
	pendingRemove?.forEach((name) => merged.delete(name));
	return Array.from(merged).sort();
};

const collectWorkspaceProfiles = (
	state: WorkspaceSecretsState,
	delta: SecretDelta,
	workspaceSlug: string
): string[] => {
	const profiles = new Set(Object.keys(state.data[workspaceSlug] ?? {}));
	Object.keys(delta.added[workspaceSlug] ?? {}).forEach((profile) => profiles.add(profile));
	Object.keys(delta.removed[workspaceSlug] ?? {}).forEach((profile) => profiles.add(profile));
	return Array.from(profiles).sort((a, b) =>
		a === "default" ? -1 : b === "default" ? 1 : a.localeCompare(b)
	);
};

// Merged view of every profile in a workspace. This preserves the optimistic
// create/delete behavior for settings pages that need grouped lists instead of
// a single profile.
export const $workspaceSecretsByProfile = atomFamily((workspaceSlug: string) => {
	const secretsByProfileAtom = atom(async (get): Promise<WorkspaceSecretsByProfileState> => {
		const state = await get($allWorkspaceSecrets);
		const delta = get($optimisticSecretDelta);
		const data: Record<string, string[]> = {};

		for (const profile of collectWorkspaceProfiles(state, delta, workspaceSlug)) {
			const names = mergeSecretNames(state, delta, workspaceSlug, profile);
			if (names.length > 0) {
				data[profile] = names;
			}
		}

		return { status: state.status, data };
	});
	secretsByProfileAtom.debugLabel = `workspaceSecretsByProfile(${workspaceSlug})`;
	return secretsByProfileAtom;
});

// Merged view of secret names for a specific workspace + profile — the
// source of truth for all UI. Combines server list + optimistic additions
// and filters out optimistic deletions. Returns the server's list status so
// callers can distinguish "no secrets" from "we couldn't check."
export const $workspaceSecretNamesState = atomFamily(
	(params: { workspaceSlug: string; profile: string }) => {
		const secretNamesStateAtom = atom(async (get): Promise<WorkspaceSecretNamesState> => {
			const state = await get($allWorkspaceSecrets);
			const delta = get($optimisticSecretDelta);
			return {
				status: state.status,
				names: mergeSecretNames(state, delta, params.workspaceSlug, params.profile),
			};
		});
		secretNamesStateAtom.debugLabel = `workspaceSecretNamesState(${params.workspaceSlug}/${params.profile})`;
		return secretNamesStateAtom;
	},
	isEqual
);

export const $workspaceSecretNames = atomFamily(
	(params: { workspaceSlug: string; profile: string }) => {
		const secretNamesAtom = atom(async (get): Promise<string[]> => {
			return (await get($workspaceSecretNamesState(params))).names;
		});
		secretNamesAtom.debugLabel = `workspaceSecretNames(${params.workspaceSlug}/${params.profile})`;
		return secretNamesAtom;
	},
	isEqual
);

// The set of profile names that currently have at least one secret defined in
// the given workspace. Populates secrets-profile override pickers.
export const $workspaceSecretProfiles = atomFamily((workspaceSlug: string) => {
	const profilesAtom = atom(async (get): Promise<string[]> => {
		const state = await get($workspaceSecretsByProfile(workspaceSlug));
		const profiles = Object.keys(state.data);
		if (!profiles.includes("default")) {
			profiles.unshift("default");
		}
		return profiles.sort((a, b) =>
			a === "default" ? -1 : b === "default" ? 1 : a.localeCompare(b)
		);
	});
	profilesAtom.debugLabel = `workspaceSecretProfiles(${workspaceSlug})`;
	return profilesAtom;
});

export const $documentSecretsproxyContext = atomFamily(
	(params: { documentId: string | undefined; environmentName: string }) => {
		const contextAtom = atom(async (get): Promise<SecretsproxyContext | undefined> => {
			if (!get($isAuthenticated)) {
				return undefined;
			}

			const override = await get($secretsProfileOverride);
			const profile = override || params.environmentName || "default";
			let workspaceSlug: string | undefined;

			if (params.documentId) {
				const document = get($document(params.documentId));
				workspaceSlug = document
					? docsListItemRemoteEntry(document)?.workspace.slug
					: undefined;
			}

			if (!workspaceSlug) {
				workspaceSlug = (await get($selectedSecretsWorkspace)) ?? undefined;
			}

			if (!workspaceSlug) {
				return undefined;
			}

			const proxySecretNames = await get(
				$workspaceSecretNamesState({ workspaceSlug, profile })
			);

			return {
				base_url: env.secretsProxyUrl,
				workspace_slug: workspaceSlug,
				profile,
				proxy_secret_names: proxySecretNames.names,
				proxy_secret_names_status: proxySecretNames.status,
			};
		});
		contextAtom.debugLabel = `documentSecretsproxyContext(${params.documentId ?? "none"}/${params.environmentName})`;
		return contextAtom;
	},
	isEqual
);

// Write atom: create/overwrite a secret. Writes an optimistic entry on
// success so the new name shows up immediately (AWS ListSecrets is eventually
// consistent and can lag several seconds behind PutSecretValue).
export const $createSecret = atom(null, async (get, set, input: CreateSecretInput) => {
	const token = await get($accessTokenFunc).getAccessTokenSilently?.();
	if (!token) {
		throw new SecretsProxyError("Not authenticated");
	}
	const api = get($secretsProxy);
	await api.secrets.create(input, token);
	const profile = input.profile && input.profile.length > 0 ? input.profile : "default";
	set($optimisticSecretDelta, (prev) =>
		addOptimistic(prev, "added", input.workspaceSlug, profile, input.tokenName)
	);
	set($allWorkspaceSecrets);
});
$createSecret.debugLabel = "createSecret";

// Write atom: delete a secret. Mirrors create — marks optimistically removed
// so the deletion is reflected immediately even if ListSecrets still echoes
// the old entry briefly.
export const $deleteSecret = atom(null, async (get, set, input: DeleteSecretInput) => {
	const token = await get($accessTokenFunc).getAccessTokenSilently?.();
	if (!token) {
		throw new SecretsProxyError("Not authenticated");
	}
	const api = get($secretsProxy);
	await api.secrets.delete(input, token);
	set($optimisticSecretDelta, (prev) =>
		addOptimistic(prev, "removed", input.workspaceSlug, input.profile, input.tokenName)
	);
	set($allWorkspaceSecrets);
});
$deleteSecret.debugLabel = "deleteSecret";

// Controls the Variables popover opening programmatically (e.g. from the
// share/publish "Manage secrets →" callout). Workspace-secret requests open the
// unified environment editor.
export type VariablesPopoverTab = "env";

export type VariablesPopoverRequest = {
	open: boolean;
	tab?: VariablesPopoverTab;
	focus?: string;
	mode?: "popover" | "environmentDialog";
	workspaceSlug?: string;
};

export const $variablesPopoverOpen = atom({ open: false } as VariablesPopoverRequest);
$variablesPopoverOpen.debugLabel = "variablesPopoverOpen";
